import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BranchManagement from './BranchManagement';
import UserAdminManagement from './UserAdminManagement';
import { supabase } from '../supabaseClient';
import { useSuperadmin } from '../hooks/useSuperadmin';

const UserManagement = () => {
  // Helper function to make authenticated fetch requests with Supabase access token
  const fetchWithAuth = async (url, options = {}) => {
    const session = await supabase.auth.getSession();
    const token = session.data?.session?.access_token;

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    return fetch(url, { ...options, headers });
  };
  const isSuperadminUser = useSuperadmin();
  const [users, setUsers] = useState([]);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
  const getUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      localStorage.setItem('user_email', user.email); // ✅ safe to set
    }
  };

  getUser();
}, []);

  const [branches, setBranches] = useState([]);
  useEffect(() => {
    // Simulate API fetch
    async function loadBranches() {
      try {
        const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/branches`);
        if (!response.ok) throw new Error('Failed to fetch branches');
        const data = await response.json(); // Expected: [{ id, name }]
        setBranches(data.branches || []);
      } catch (error) {
        console.error('Error fetching branches:', error);
        setBranches([]);
      }
    }
    loadBranches();
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [branchId, setBranchId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', limit: '', endDate: '', branchId: '',price: null, isPaid: false });
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [userToDelete, setUserToDelete] = useState(null);
  const [pendingEmails, setPendingEmails] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // New state for password reset modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [userToReset, setUserToReset] = useState(null);
  const [passwordReady, setPasswordReady] = useState(false);

  // Load users function extracted for reuse
  const loadUsers = async (currentOffset = offset) => {
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_URL}/api/users?offset=${currentOffset}&pageSize=${pageSize}&search=${searchQuery}&sortDesc=${sortDirection === 'DESC'}&sortBy=${sortKey}${branchId ? `&branchId=${branchId}` : ''}`
      );
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data.users || []);
      setHasMore(data.hasMore);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  useEffect(() => {
    loadUsers(offset);
  }, [offset, searchQuery, sortDirection, sortKey, branchId, pageSize]);

  const handleDeleteConfirmed = async () => {
    if (!userToDelete) return;
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_URL}/api/users/${userToDelete}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Failed to delete user');
      setUsers(prev => prev.filter(user => user.id !== userToDelete && user.userId !== userToDelete));
      await loadUsers(offset);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Error deleting user: ' + error.message);
    } finally {
      setUserToDelete(null);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setNewUser({
      email: user.email,
      password: '',
      limit: user.totalLimit,
      endDate: user.endDate ? user.endDate.split('T')[0] : '',
      branchId: user.branchId || '',
      isPaid: user.isPaid || false,
      price: user.price || null,
    });
    setShowModal(true);
  };

  const handleResetPassword = async (userId) => {
    if (!userId) return;
    try {
      const newPassword = Math.random().toString(36).slice(-8); // Generate a random 8-char password
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}/resetpassword`,
        { method: 'POST', body: JSON.stringify({ newPassword: newPassword }) }
      );
      if (!response.ok) throw new Error('Failed to reset password');
      setResetPassword(newPassword);
      setPasswordReady(true);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Error resetting password: ' + error.message);
      setShowPasswordModal(false);
      setResetPassword('');
      setUserToReset(null);
      setPasswordReady(false);
    }
  };

  const handleCreate = () => {
    // If editing, skip confirmation and update directly
    if (editingUser) {
      confirmCreateUsers([newUser.email]);
      return;
    }

    const count = newUser.count && Number.isInteger(newUser.count) && newUser.count > 1 ? newUser.count : 1;
    let emailList = [];

    if (count > 1) {
      const emailRegex = /^([^@0-9]*)(\d+)(@.+)$/;
      const match = newUser.email.match(emailRegex);
      if (!match) {
        toast.error("For multiple users, email must contain a numeric part, e.g. john1@example.com");
        return;
      }
      const prefix = match[1];
      const startNum = parseInt(match[2], 10);
      const domain = match[3];
      for (let i = 0; i < count; i++) {
        emailList.push(`${prefix}${startNum + i}${domain}`);
      }
    } else {
      emailList.push(newUser.email);
    }

    setPendingEmails(emailList);
    setShowConfirmModal(true);
  };

  const confirmCreateUsers = async (emails = pendingEmails) => {
    try {
      const method = editingUser ? 'PUT' : 'POST';
      const url = editingUser
        ? `${process.env.REACT_APP_API_URL}/api/users/${editingUser.userId}`
        : `${process.env.REACT_APP_API_URL}/api/users`;

      const createdUsers = [];

      for (const email of emails) {
        const userObj = { ...newUser, email };
        delete userObj.count;

        const response = await fetchWithAuth(url, {
          method,
          body: JSON.stringify(userObj),
        });
        if (!response.ok) throw new Error(`Failed to save user ${email}`);
        const savedUser = await response.json();
        createdUsers.push(savedUser);
      }

      if (editingUser) {
        setUsers(users.map(u => u.userId === editingUser.userId ? createdUsers[0] : u));
      } else {
        setUsers([...users, ...createdUsers]);
      }

      toast.success(
        createdUsers.length > 1
          ? `Created ${createdUsers.length} users successfully`
          : editingUser
          ? 'User updated successfully'
          : 'User created successfully'
      );
      await loadUsers(offset);
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setPendingEmails([]);
      setShowConfirmModal(false);
      setShowModal(false);
      setEditingUser(null);
      setNewUser({ email: '', password: '', limit: '', endDate: '', branch: '',price: null, isPaid: false });
    }
  };

  // Simulate loading state for demonstration
  const [isLoading, setIsLoading] = useState(false);

  // Example: Set loading to true for 1s on mount (remove in prod)
  useEffect(() => {
    setIsLoading(true);
    const t = setTimeout(() => setIsLoading(false), 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .spinner {
            border: 4px solid #f3f3f3;
            border-top: 4px solid #2563eb;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            animation: spin 1s linear infinite;
            margin: auto;
          }
        `}
      </style>
      <div style={{ 
        padding: '30px', 
        margin: '20px auto', 
        backgroundColor: '#fff', 
        borderRadius: '8px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: '1200px',
        width: '100%',
        minWidth: '600px',
      }}>
      <h1>User Management</h1>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          className={`tab-button ${activeTab === 'users' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`tab-button ${activeTab === 'branches' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('branches')}
        >
          Branches
        </button>
        {isSuperadminUser && (
          <button
            className={`tab-button ${activeTab === 'admins' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('admins')}
          >
            Admin Users
          </button>
        )}
      </div>

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '10px' }}>
          <div className="spinner" />
        </div>
      )}

      {activeTab === 'users' && !isLoading && (
        <>
          <button onClick={() => {
            setNewUser({
              email: '',
              password: '',
              limit: '',
              endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              branch: '',
              price: null,
              isPaid: false
            });
            setEditingUser(null);
            setShowModal(true);
          }} className="create-button">Create New User</button>

          <input
            type="text"
            placeholder="Search by name or email"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <select
            value={branchId || ''}
            onChange={e => setBranchId(e.target.value || null)}
            style={{ marginBottom: '10px', padding: '8px', marginLeft: '10px' }}
          >
            <option value="">All Branches</option>
            {branches.map(branch => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
          <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={{ marginBottom: '10px', padding: '8px' }}>
            <option value="email">Sort by Email</option>
            <option value="branchName">Sort by Branch</option>
            <option value="createdAt">Sort by Created Date</option>
          </select>
          <select value={sortDirection} onChange={e => setSortDirection(e.target.value)} style={{ marginBottom: '10px', padding: '8px' }}>
            <option value="ASC">ASC</option>
            <option value="DESC">DESC</option>
          </select>
          <select value={pageSize} onChange={e => setPageSize(parseInt(e.target.value))} style={{ marginBottom: '10px', padding: '8px', marginLeft: '10px' }}>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
            <thead>
              <tr>
                <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px' }}>#</th>
                <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px' }}>Email</th>
                <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px' }}>Created Date</th>
                <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px' }}>Branch</th>
                <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px' }}>Search</th>
                <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px' }}>Limit</th>
                <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px' }}>End Date</th>
                <th style={{ borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users
                //.filter(user => user.email?.toLowerCase().includes(searchQuery.toLowerCase()) )
                .sort((a, b) => {
                  const aVal = (a[sortKey] || '').toLowerCase();
                  const bVal = (b[sortKey] || '').toLowerCase();
                  const result = aVal.localeCompare(bVal);
                  return sortDirection === 'ASC' ? result : -result;
                })
                .map((user, index) => (
                  <tr key={user.userId || `row-${index}`}>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>{offset + index + 1}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>{user.email || 'N/A'}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>{user.branchName || 'N/A'}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>{user.totalSearch}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>{user.totalLimit}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                      {user.endDate ? new Date(user.endDate).toLocaleDateString('en-GB') : 'N/A'}
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                      <button onClick={() => handleEdit(user)} className="edit-button">Edit</button>
                      {isSuperadminUser && (
                      <button onClick={() => setUserToDelete(user.userId)} className="delete-button">Delete</button>
                      )}
                      {isSuperadminUser && (
                        <button
                          onClick={() => {
                            setUserToReset(user.userId);
                            setShowPasswordModal(true);
                            setResetPassword('');
                            setPasswordReady(false);
                          }}
                          className="reset-button"
                        >
                          Reset Password
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
            <button
              disabled={offset === 0}
              onClick={() => setOffset(offset - pageSize)}
              className="tab-button"
            >
              Previous
            </button>
            <button
              disabled={!hasMore}
              onClick={() => setOffset(offset + pageSize)}
              className="tab-button"
            >
              Next
            </button>
          </div>

          {showModal && (
            <div className="modal-overlay">
              <div className="modal">
                <h2 style={{ marginBottom: '24px', fontWeight: 700 }}>{editingUser ? 'Edit User' : 'Add New User'}</h2>
                <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label className="modal-label">Branch</label>
                      <select
                        value={newUser.branchId?.toString() || ''}
                        onChange={e => setNewUser({ ...newUser, branchId: e.target.value })}
                        required
                        className="modal-input"
                      >
                        <option value="">Select Branch</option>
                        {branches.map(branch => (
                          <option key={branch.id} value={branch.id.toString()}>{branch.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="modal-field">
                      <label className="modal-label">Number of Users</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        placeholder="1"
                        value={newUser.count || 1}
                        onChange={e => setNewUser({ ...newUser, count: parseInt(e.target.value) })}
                        required
                        className="modal-input"
                      />
                    </div>
                    <div className="modal-field">
                      <label className="modal-label">Email</label>
                      <input
                        type="email"
                        placeholder="Email"
                        value={newUser.email}
                        onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                        required={!editingUser}
                        disabled={!!editingUser}
                        className="modal-input"
                      />
                    </div>
                    <div className="modal-field">
                      <label className="modal-label">Password</label>
                      <input
                        type="password"
                        placeholder="Password"
                        value={newUser.password}
                        onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                        required={!editingUser}
                        disabled={!!editingUser}
                        className="modal-input"
                      />
                    </div>

                    <div className="modal-field">
                      <label className="modal-label">Limit Search</label>
                      <input
                        type="number"
                        placeholder="Limit"
                        value={newUser.limit}
                        onChange={e => setNewUser({ ...newUser, limit: e.target.value })}
                        required
                        className="modal-input"
                      />
                    </div>
                    <div className="modal-field">
                      <label className="modal-label">End Date</label>
                      <input
                        type="date"
                        placeholder="End Date"
                        value={newUser.endDate ? newUser.endDate.split('T')[0] : newUser.endDate}
                        onChange={e => setNewUser({ ...newUser, endDate: e.target.value })}
                        required
                        className="modal-input"
                      />
                    </div>
                                      <div className="modal-field">
                     <label className="modal-label">Price of Purchase</label>
                    <input
                      type="number"
                      placeholder="500,000"
                      value={newUser.price || ''}
                      onChange={e => setNewUser({ ...newUser, price: e.target.value })}
                      className="modal-input"
                    />
                  </div>

                  <div className="modal-field">
                    <label className="modal-label">Paid</label>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={!!newUser.isPaid}
                        onChange={e => setNewUser({ ...newUser, isPaid: e.target.checked })}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  </div>
                  <div className="modal-actions">
                    <button type="submit" className="modal-button">
                      {editingUser ? 'Update User' : 'Add User'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setNewUser({ email: '', password: '', limit: '', endDate: '', branch: '', price: null, isPaid: false });
                        setEditingUser(null);
                        setShowModal(false);
                      }}
                      className="cancel-button"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'admins' && <UserAdminManagement />}

      {activeTab === 'branches' && <BranchManagement />}

      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Creation</h3>
            <p>The following users will be created:</p>
            <ul>
              {pendingEmails.map((email, idx) => (
                <li key={idx}>{email}</li>
              ))}
            </ul>
            <div className="modal-actions">
              <button onClick={() => confirmCreateUsers()} className="confirm-button">Confirm</button>
              <button onClick={() => { setShowConfirmModal(false); setPendingEmails([]); }} className="cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {userToDelete && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this user?</p>
            <div className="modal-actions">
              <button onClick={handleDeleteConfirmed} className="confirm-button">Yes, Delete</button>
              <button onClick={() => setUserToDelete(null)} className="cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            {!passwordReady ? (
              <>
                <h3>Reset Password</h3>
                <p>Are you sure you want to reset this user's password?</p>
                <div className="modal-actions">
                  <button
                    onClick={() => handleResetPassword(userToReset)}
                    className="confirm-button"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setResetPassword('');
                      setUserToReset(null);
                      setPasswordReady(false);
                    }}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>Password Reset Successful</h3>
                <p>Here is the new password:</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 0' }}>
                  <input
                    type="text"
                    readOnly
                    value={resetPassword}
                    style={{ flex: 1, padding: '6px', fontFamily: 'monospace' }}
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(resetPassword);
                      toast.success('Password copied to clipboard');
                    }}
                    className="confirm-button"
                  >
                    Copy
                  </button>
                </div>
                <div className="modal-actions">
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setResetPassword('');
                      setUserToReset(null);
                      setPasswordReady(false);
                    }}
                    className="cancel-button"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </div>
    </>
  );
};


export default UserManagement;